import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { signInWithCustomToken, hideAuthMessage } from 'redux/actions/Auth';
import { APP_PREFIX_PATH } from 'configs/AppConfig';

// Mensaje único para cualquier fallo del login LTI (a pedido).
const LTI_ERROR_MESSAGE = 'No pudimos iniciar tu sesión. Intenta volver a entrar o contacta a soporte.';
// Timeout de seguridad: si tras este tiempo no se completó el login, mostrar el error
// en vez de dejar al estudiante atascado en "Procesando…".
const AUTH_TIMEOUT_MS = 15000;

const LtiSso = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [status, setStatus] = useState('processing');

  // Capturamos el token UNA sola vez, al montar. Antes se releía de la URL en cada
  // re-ejecución del efecto; como el token se borra de la URL tras despacharlo, el
  // segundo paso veía "sin token" y mostraba falsamente el estado 'missing' +
  // redirect a login (incluso cuando el login ya estaba en curso o completado).
  const customTokenRef = useRef(null);
  if (customTokenRef.current === null) {
    customTokenRef.current = new URLSearchParams(window.location.search).get('token') || '';
  }

  // Señales de Redux:
  // - secondaryApp: se setea SOLO cuando el login (incl. DB secundaria) fue exitoso.
  //   Es más confiable que `token`, que se inicializa desde localStorage y puede venir viejo.
  // - showMessage/messageType: cualquier showAuthMessage de error/aviso en la saga.
  const secondaryApp = useSelector(state => state.auth.secondaryApp);
  const showMessage = useSelector(state => state.auth.showMessage);
  const messageType = useSelector(state => state.auth.messageType);

  // Arranque: corre una sola vez. Si hay token, lo despacha y lo limpia de la URL.
  // Si no hay token, recién ahí redirige a login (cancelable en el cleanup).
  useEffect(() => {
    const customToken = customTokenRef.current;

    if (!customToken) {
      setStatus('missing');
      const timer = setTimeout(() => history.replace('/auth/login'), 800);
      return () => clearTimeout(timer);
    }

    // Limpiar cualquier mensaje de error previo para que la detección de error
    // refleje únicamente este intento de login LTI.
    dispatch(hideAuthMessage());
    // Reiniciar el flag del modal de bienvenida: debe mostrarse en CADA launch LTI
    // (no una sola vez por usuario), pero no en cada navegación interna.
    sessionStorage.removeItem('lti_welcome_shown');
    // Dispatch the saga action to sign in with custom token
    dispatch(signInWithCustomToken(customToken));
    // Remove token from URL (no afecta a customTokenRef, ya capturado)
    window.history.replaceState({}, document.title, window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Éxito: login secundario listo → redirigir.
  // Si el context.label del launch no era válido (labelValid === false), en vez de
  // entrar a la app se redirige a la página de error de curso inválido.
  useEffect(() => {
    if (customTokenRef.current && secondaryApp) {
      let contextValid = true;
      try {
        const claims = JSON.parse(sessionStorage.getItem('lti_claims') || 'null');
        if (claims && claims.labelValid === false) contextValid = false;
      } catch (e) {
        /* sin claims legibles → no bloquear */
      }
      setStatus('signed-in');
      const dest = contextValid ? APP_PREFIX_PATH : '/auth/lti-curso-invalido';
      const timer = setTimeout(() => history.replace(dest), 300);
      return () => clearTimeout(timer);
    }
  }, [secondaryApp, history]);

  // Error: la saga despachó un mensaje de error/aviso y aún no hay login exitoso.
  useEffect(() => {
    if (
      customTokenRef.current &&
      !secondaryApp &&
      showMessage &&
      (messageType === 'error' || messageType === 'warning')
    ) {
      setStatus('error');
    }
  }, [showMessage, messageType, secondaryApp]);

  // Timeout de seguridad: si al expirar no se autenticó, mostrar el error.
  useEffect(() => {
    if (!customTokenRef.current) return undefined;
    const timer = setTimeout(() => {
      setStatus((prev) => (prev === 'signed-in' ? prev : 'error'));
    }, AUTH_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{ padding: 40 }}>
      {status === 'processing' && <h3>Procesando inicio de sesión LTI...</h3>}
      {status === 'missing' && <h3>Cargando simuladores...</h3>}
      {status === 'signed-in' && <h3>Inicio de sesión completado. Redirigiendo...</h3>}
      {status === 'error' && <h3>{LTI_ERROR_MESSAGE}</h3>}
    </div>
  );
};

export default LtiSso;
